// From: https://github.com/modusign/modusign-client/blob/2772938d47982e2e868b5adbce601cdf9bb0b288/app/src/containers/RequesterAttachmentsCard/index.tsx

import {
  faArrowUpFromBracket,
  faCaretDown,
  faCaretUp,
  faQuestionCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Flex, Text, Tooltip } from '@modusign/design-system';
import { iconNeutralVeryDark } from '@modusign/design-system/colors';
import { useAttachments, useRequester } from '@modusign/react-signing';
import { AttachmentToReference } from '@modusign/react-signing/types';
import { AxiosError } from 'axios';
import * as CSS from 'csstype';
import { useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useRendersCount, useToggle } from 'react-use';
import { ResponsiveValue } from 'styled-system';
import { v4 } from 'uuid';

import FileUploadErrorModal from '@app/components/FileUploadErrorModal';
import {
  FileUploadErrorBuilder,
  IFileUploadError,
} from '@app/components/FileUploadErrorModal/fileUploadErrorBuilder';
import PopUpBlockModal from '@app/components/Modal/PopUpBlockModal';
import { createDataLabel } from '@app/features/documents/utils/createDataLabel';
import useUsageCount from '@app/hooks/useUsageCount';
import getExtensionFromFileName from '@app/libs/file/getExtensionFromFileName';
import getFileNameExcludeExtension from '@app/libs/file/getFileNameExcludeExtension';
import { validateAttachmentFile } from '@app/libs/file/validateFile';
import { postAttachmentUploadViaPresignedUrl } from '@app/store/transport-layer/upload/attachments/upload-url';

import RequesterAttachmentList from './RequesterAttachmentList';

const maxFileSize = 10 * 1000 * 1000;

/**
 * 관련 PR: https://github.com/modusign/modusign-client/pull/7670#issue-1493620187
 *
 * 일반서명과 링크서명에서 요청자 첨부파일 업로드 카드의 UI와 서명자 첨부파일 저장 store가 서로 다릅니다.
 * 이는 모두 차세대때 통합될 예정이라서 컴포넌트를 분리하거나 커스텀 훅을 생성하지 않고
 * props를 이용해 스타일, signerAttachmentCount, hasAttachmentLimitReached등을 받아 분기처리를 해주도록 하였습니다.
 */

interface RequesterAttachmentsCardProps {
  displayDataLabel?: boolean;
  hasAttachmentLimitReached: boolean;
  headerFontSize?: ResponsiveValue<'xs' | 'sm' | 'md' | 'lg' | CSS.Property.FontSize> | number;
  headerFontWeight?: ResponsiveValue<'regular' | 'medium' | 'bold' | CSS.Property.FontWeight>;
  headerMarginBottom?: number;
  cardInnerPadding?: number;
}

export default function RequesterAttachmentsCard({
  displayDataLabel,
  cardInnerPadding = 6,
  hasAttachmentLimitReached,
  headerFontSize = 2,
  headerFontWeight = 'medium',
  headerMarginBottom = 4,
}: RequesterAttachmentsCardProps) {
  const { attachments, addAttachment } = useAttachments();

  const { attachmentRequestCount } = useUsageCount();

  const requesterAttachments = attachments.filter(
    (a) => a.participantReference.type === 'REQUESTER'
  );
  const signerAttachmentCount = attachments.filter(
    (a) => a.participantReference.type === 'SIGNER'
  ).length;
  const { requester } = useRequester();

  const [expanded, toggleExpanded] = useToggle(false);
  const [isPopUpBlockModalOpen, setPopUpBolckModal] = useToggle(false);
  const renderCount = useRendersCount();

  const inputRef = useRef<HTMLInputElement>(null);

  const [errors, setErrors] = useState<IFileUploadError[]>([]);

  const { mutate: uploadAttachments, isLoading } = useMutation<
    { succeeded: AttachmentToReference[]; failed: IFileUploadError[] },
    AxiosError,
    File[]
  >(
    async (files: File[]) => {
      const tempDocumentId = v4();
      const succeeded: AttachmentToReference[] = [];
      const errors: IFileUploadError[] = [];

      const PQueue = (await import('p-queue')).default; // eslint-disable-line import/no-unresolved
      const queue = new PQueue({ concurrency: 3 });

      queue.on(
        'completed',
        ({
          fileId,
          file,
          previewUrl,
          token,
        }: {
          fileId: string;
          file: File;
          previewUrl: string;
          token: string;
        }) => {
          succeeded.push({
            participantId: requester?.id || '',
            title: getFileNameExcludeExtension(file.name),
            message: null,
            file: {
              extension: getExtensionFromFileName(file.name),
              name: getFileNameExcludeExtension(file.name),
              size: file.size,
              url: previewUrl,
            },
            documentId: tempDocumentId,
            id: v4(),
            input: {
              fileId,
              token,
              fileName: getFileNameExcludeExtension(file.name),
            },
            dataLabel: createDataLabel(),
            config: {
              required: true,
            },
          });
        }
      );

      let currentAttachmentCount = requesterAttachments.length + signerAttachmentCount;

      await Promise.all(
        files.map(async (file) => {
          const errorBuilder = new FileUploadErrorBuilder(file.name);

          const rexExp = /[/?<>\\":*|₩]/g;
          if (rexExp.test(file.name)) {
            const invalidCharacters = '/?<>":*|₩';
            errorBuilder.fileNameValidationError(invalidCharacters);
          }

          const isValidFile = await validateAttachmentFile(file);
          if (!isValidFile) {
            errorBuilder.fileExtensionError();
          }

          if (file.size > maxFileSize) {
            errorBuilder.fileSizeExceededError(maxFileSize);
          }

          if (file.name.length > 100) {
            errorBuilder.fileNameLengthExceededError(100);
          }

          if (currentAttachmentCount >= attachmentRequestCount.limit) {
            errorBuilder.fileNumberExceededError(attachmentRequestCount.limit);
          }

          const error = errorBuilder.build();
          if (error) {
            errors.push(error);
          } else {
            currentAttachmentCount += 1;
            queue.add(() => {
              const extension = getExtensionFromFileName(file.name);
              return postAttachmentUploadViaPresignedUrl({ extension, file })
                .then((response) => ({ ...response, file }))
                .catch((e) =>
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  errors.push(new FileUploadErrorBuilder(file.name).otherError(e.message).build()!)
                );
            });
          }
        })
      );

      await queue.onIdle();
      return { succeeded, failed: errors };
    },
    {
      mutationKey: 'requesterAttachmentUpload',
      onSuccess: ({ succeeded, failed }) => {
        succeeded.forEach(addAttachment);
        setErrors(failed);
      },
    }
  );

  return (
    <Box backgroundColor="#D2D2D2" borderRadius="3px" p={4}>
      <input
        hidden
        multiple
        ref={inputRef}
        type="file"
        onChange={(e) => {
          const { files } = e.target;

          if (files && files.length > 0) {
            uploadAttachments(Array.from(files));
          }
          e.target.value = '';
        }}
      />
      <Box
        backgroundColor="white"
        borderRadius="3px"
        boxShadow="0 1px 3px 0 rgba(0,0,0,0.2)"
        p={cardInnerPadding}
      >
        <Flex
          alignItems="center"
          flexDirection="row"
          justifyContent="space-between"
          mb={requesterAttachments.length > 0 ? headerMarginBottom : 0}
        >
          <Flex alignItems="center">
            <Text fontSize={headerFontSize} fontWeight={headerFontWeight}>
              첨부파일
            </Text>
            <Tooltip content="서명 요청 시 서명자에게 전달할 파일을 미리 첨부할 수 있습니다.">
              <FontAwesomeIcon
                icon={faQuestionCircle}
                style={{
                  color: '#848484',
                  marginLeft: '4px',
                }}
              />
            </Tooltip>
          </Flex>
          <Tooltip
            content={
              <Box fontSize="13px" lineHeight={1.5} textAlign="center">
                <div>
                  한 문서 안에서 이용할 수 있는
                  <br />
                  첨부파일 {attachmentRequestCount.limit}개를 모두 이용하셨습니다.
                </div>
              </Box>
            }
            disabled={!hasAttachmentLimitReached}
            key={renderCount}
            placement="bottom"
            scale="md"
          >
            <Button
              disabled={hasAttachmentLimitReached}
              isLoading={isLoading}
              weight="light"
              width="98px"
              onClick={() => {
                inputRef.current?.click();
              }}
            >
              <FontAwesomeIcon
                fill={iconNeutralVeryDark}
                icon={faArrowUpFromBracket}
                size="xs"
                style={{ marginRight: '3px' }}
              />
              업로드
            </Button>
          </Tooltip>
        </Flex>
        {requesterAttachments.length > 2 && (
          <Flex
            alignItems="center"
            borderTop="1px solid"
            borderTopColor="lightGray500"
            justifyContent="space-between"
            mb={expanded ? 3 : 0}
            py="10px"
          >
            <Text>
              첨부한 파일{' '}
              <Text color="textEmphasisBase" fontWeight="bold">
                ({requesterAttachments.length}개)
              </Text>
            </Text>
            <Box
              aria-label={`첨부파일 목록 ${expanded ? '닫기' : '열기'}`}
              as="button"
              color="iconNeutralVeryDark"
              type="button"
              width="21px"
              onClick={toggleExpanded}
            >
              <FontAwesomeIcon icon={expanded ? faCaretUp : faCaretDown} />
            </Box>
          </Flex>
        )}
        {(requesterAttachments.length <= 2 || expanded) && (
          <RequesterAttachmentList
            displayDataLabel={displayDataLabel}
            onBlockPopUp={setPopUpBolckModal}
          />
        )}
      </Box>
      <PopUpBlockModal isOpen={isPopUpBlockModalOpen} onConfirm={setPopUpBolckModal} />
      <FileUploadErrorModal errors={errors} onConfirm={() => setErrors([])} />
    </Box>
  );
}
